import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, LOGO, errMsg, api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useRecaptcha } from "../lib/useRecaptcha";

export default function AuthPage() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();
  const { config: rcConfig, widgetRef, getTokens, reset: rcReset } = useRecaptcha();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let recaptcha = {};
      try { recaptcha = await getTokens(); }
      catch (rcErr) { toast.error(rcErr.message); setBusy(false); return; }
      const { data } = await axios.post(`${API}/auth/login`, { ...form, recaptcha });
      login(data.token, data.user);
      if (data.user.must_change_password) {
        setMustChange(true);
        toast.info("Por seguridad, establece tu nueva contraseña antes de continuar.");
      } else {
        toast.success(`¡Bienvenido/a, ${data.user.name}!`);
        nav("/app");
      }
    } catch (err) {
      toast.error(errMsg(err));
      rcReset();
    } finally {
      setBusy(false);
    }
  };

  const changeInitial = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/force-password", { new_password: newPassword });
      toast.success("Contraseña actualizada. ¡Bienvenido/a!");
      nav("/app");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
      <div className="w-full max-w-md fade-up">
        <Link to="/"><img src={LOGO} alt="Natur Autoescuela" className="h-28 mx-auto mb-8 object-contain" /></Link>
        <div className="bg-white border border-neutral-200 rounded-2xl p-8 shadow-sm">
          {!mustChange ? (
            <>
              <h1 className="text-2xl font-semibold tracking-tight mb-1">Acceso alumnos</h1>
              <p className="text-sm text-neutral-500 mb-6">
                Accede con las credenciales facilitadas por Natur Autoescuela.
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email">User data</Label>
                  <Input id="email" type="email" placeholder="xxxxxxx" data-testid="auth-email-input" value={form.email} onChange={set("email")} required className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="password">Authorization Code</Label>
                  <Input id="password" type="password" data-testid="auth-password-input" value={form.password} onChange={set("password")} required className="mt-1.5" />
                </div>
                {(rcConfig.mode === "v2" || rcConfig.mode === "both") && (
                  <div ref={widgetRef} data-testid="recaptcha-v2-widget" className="flex justify-center" />
                )}
                <Button type="submit" disabled={busy} data-testid="auth-submit-btn"
                        className="w-full rounded-full bg-orange-600 hover:bg-orange-700 text-white h-11">
                  {busy ? "Un momento…" : "Entrar"}
                </Button>
              </form>
              <p className="mt-5 text-xs text-neutral-500 text-center">
                ¿Aún no eres alumno/a?{" "}
                <a href="https://naturautoescuela.es" target="_blank" rel="noreferrer" data-testid="auth-info-link"
                   className="text-orange-700 hover:underline">Solicitar información</a>
              </p>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-semibold tracking-tight mb-1">Primer acceso</h1>
              <p className="text-sm text-neutral-500 mb-6">
                Tu contraseña es temporal. Establece una nueva para continuar.
              </p>
              <form onSubmit={changeInitial} className="space-y-4" data-testid="force-password-form">
                <div>
                  <Label htmlFor="newpw">Nueva contraseña</Label>
                  <Input id="newpw" type="password" data-testid="auth-new-password-input" value={newPassword}
                         onChange={(e) => setNewPassword(e.target.value)} required minLength={6} className="mt-1.5" />
                </div>
                <Button type="submit" disabled={busy || newPassword.length < 6} data-testid="auth-force-password-btn"
                        className="w-full rounded-full bg-orange-600 hover:bg-orange-700 text-white h-11">
                  {busy ? "Guardando…" : "Guardar y entrar"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
